<template>
  <v-layout wrap>
    <v-flex xs12 xl6 pa-2>
      <v-card pb-2>
        <Subheader title="Grade Values" v-model="searchValue" search="Grade Values">
          <v-btn color="add" @click="addValue()">
            <v-icon class="mr-1">add</v-icon>
            {{ $t('Button.Add') }}
          </v-btn>
        </Subheader>
        <v-card-text>
          <v-alert color="info" icon="info" :value="true" outline>
            Record continuous database data grade values that should be displayed. Any data grade values not included in this list will not be shown to the client. Once a continuous database data grade is recorded in this list, you can map the value to an output value in the <code>Grade Output Configuration</code> table.
          </v-alert>
          <v-data-table :headers="valueHeaders" :items="ContinuousDataGrades" :search="searchValue" hide-actions>
            <template slot="headerCell" slot-scope="props">
              <v-tooltip bottom v-if="props.header.showtooltip">
                <template v-slot:activator="{ on }">
                  <span v-on="on" v-html="props.header.text"></span>
                </template>
                <v-card-text>{{$t('ContinuousDataGradesManage.menuTextYes')}}</v-card-text>
                <v-card-text>{{$t('ContinuousDataGradesManage.menuTextNo')}}</v-card-text>
              </v-tooltip>
              <span v-else>{{ props.header.text }}</span>
            </template>
            <template slot="items" slot-scope="props">
              <td class="text-xs-left">{{ props.item.GradeValue }}</td>
              <td class="text-xs-left">
                <span v-if="props.item.ShouldFilter">
                  <v-chip disabled outline small color="error">filtered out</v-chip>
                </span>
                <span v-else>
                  <v-chip v-if="!haveConfiguration(props.item)" disabled outline small color="warning">output configuration needed</v-chip>
                  <v-chip v-else disabled outline small color="info">-</v-chip>
                </span>
              </td>
              <td class="text-xs-right">
                <v-flex>
                  <EditButton small @click="editValue(props.item)">
                    {{ $t('Button.Edit') }}
                  </EditButton>
                  <DeleteButton small @click="deleteValue(props.item)">
                    {{ $t('Button.Delete') }}
                  </DeleteButton>
                </v-flex>
              </td>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-flex>
    <br />
    <v-flex xs12 xl6 pa-2>
      <v-card>
        <Subheader title="Grade Output Configuration" v-model="searchOutput" search="Output Configuration">
          <v-btn color="add" @click="addOutput()">
            <v-icon class="mr-1">add</v-icon>
            {{ $t('Button.Add') }}
          </v-btn>
        </Subheader>
        <v-card-text>
          <v-alert color="info" icon="info" :value="true" outline>
            Designate client-facing data grade output values. Each output value can be mapped to multiple continuous database data grade value. Once mapped, the continuous database grade value will be translated to the code and description specified in this table in output reports and R client.
          </v-alert>
          <v-data-table :headers="outputHeaders" :items="ContinuousGradeConfigurations" :search="searchOutput" hide-actions>
            <template slot="items" slot-scope="props">
              <td class="text-xs-left">{{ props.item.Code }}</td>
              <td class="text-xs-left">{{ props.item.Description }}</td>
              <td class="text-xs-left" v-html="getValueString(props.item.ContinuousDataGrades).toString()"></td>
              <td class="text-xs-right">
                <v-flex>
                  <EditButton small @click="editOutput(props.item)">
                    {{ $t('Button.Edit') }}
                  </EditButton>
                  <DeleteButton small @click="deleteOutput(props.item)">
                    {{ $t('Button.Delete') }}
                  </DeleteButton>
                </v-flex>
              </td>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-flex>

    <app-modal v-model="addValueModal" max-width="800" persistent>
      <template v-slot:title>
        Add Grade Value
      </template>
      <template v-slot:text>
        <v-form v-model="validValue" ref="formValue" lazy-validation>
          <v-text-field label="Grade Value" v-model="addGradeValue.GradeValue" :rules="gradeValueRule"></v-text-field>
          Should Filter out:
          <v-menu offset-y open-on-hover :open-delay="400">
            <template v-slot:activator="{ on }">
              <v-icon small v-on="on" class="clickable ml-2" color="primary">info_outline</v-icon>
            </template>
            <v-card>
              <v-card-text>{{$t('ContinuousDataGradesManage.menuTextYes')}}</v-card-text>
              <v-card-text>{{$t('ContinuousDataGradesManage.menuTextNo')}}</v-card-text>
            </v-card>
          </v-menu>
          <v-switch v-model="addGradeValue.ShouldFilter" hide-details :label="`${getFilterString(addGradeValue.ShouldFilter).toString()}`"></v-switch>
        </v-form>
      </template>
      <template v-slot:actions>
        <v-spacer></v-spacer>
        <v-btn color="success" @click="submitAddValue()">
          <v-icon>done</v-icon>{{ $t('Button.Save') }}
        </v-btn>
        <v-btn color="secondary" @click="addValueModal = false" outline>{{ $t('Button.Cancel') }}</v-btn>
      </template>
    </app-modal>

    <app-modal v-model="addOutputModal" max-width="800" persistent>
      <template v-slot:title>
        Add Grade Output Configuration
      </template>
      <template v-slot:text>
        <v-form v-model="validOutput" ref="formOutput" lazy-validation>
          <v-text-field label="Code" v-model="addGradeOutput.Code" :rules="nullRule"></v-text-field>
          <v-text-field label="Description" v-model="addGradeOutput.Description" :rules="nullRule"></v-text-field>
          <v-select label="Value" v-model="addGradeOutput.Values" :items="allValueItem" multiple :rules="nullRuleSelect"></v-select>
        </v-form>
      </template>
      <template v-slot:actions>
        <v-spacer></v-spacer>
        <v-btn color="success" @click="submitAddOutput()">
          <v-icon>done</v-icon>{{ $t('Button.Save') }}
        </v-btn>
        <v-btn color="secondary" @click="addOutputModal = false" outline>{{ $t('Button.Cancel') }}</v-btn>
      </template>
    </app-modal>

    <app-modal v-model="editValueModal" max-width="800" persistent>
      <template v-slot:title>
        Edit Grade Value
      </template>
      <template v-slot:text>
        <v-text-field label="Grade Value" v-model="editGradeValue.GradeValue" :rules="gradeValueRule"></v-text-field>
        Should Filter out:
        <v-menu offset-y open-on-hover :open-delay="400">
          <template v-slot:activator="{ on }">
            <v-icon small v-on="on" class="clickable ml-2" color="primary">info_outline</v-icon>
          </template>
          <v-card>
            <v-card-text>{{$t('ContinuousDataGradesManage.menuTextYes')}}</v-card-text>
            <v-card-text>{{$t('ContinuousDataGradesManage.menuTextNo')}}</v-card-text>
          </v-card>
        </v-menu>
        <v-switch v-model="editGradeValue.ShouldFilter" hide-details :label="`${getFilterString(editGradeValue.ShouldFilter).toString()}`"></v-switch>
      </template>
      <template v-slot:actions>
        <v-spacer></v-spacer>
        <v-btn color="success" @click="submitEditValue()">
          <v-icon>done</v-icon>{{ $t('Button.Save') }}
        </v-btn>
        <v-btn color="secondary" @click="editValueModal = false" outline>{{ $t('Button.Cancel') }}</v-btn>
      </template>
    </app-modal>

    <app-modal v-model="editOutputModal" max-width="800" persistent>
      <template v-slot:title>
        Edit Grade Output Configuration
      </template>
      <template v-slot:text>
        <v-text-field label="Code" v-model="editGradeOutput.Code" :rules="nullRule"></v-text-field>
        <v-text-field label="Description" v-model="editGradeOutput.Description" :rules="nullRule"></v-text-field>
        <v-select label="Value" v-model="editGradeOutput.Values" :items="editOutputValueItem" multiple :rules="nullRuleSelect"></v-select>
      </template>
      <template v-slot:actions>
        <v-spacer></v-spacer>
        <v-btn color="success" @click="submitEditOutput()">
          <v-icon>done</v-icon>{{ $t('Button.Save') }}
        </v-btn>
        <v-btn color="secondary" @click="editOutputModal = false" outline>{{ $t('Button.Cancel') }}</v-btn>
      </template>
    </app-modal>

    <app-modal v-model="deleteValueModal" max-width="800" persistent>
      <template v-slot:title>
        Delete Grade Value
      </template>
      <template v-slot:text>
        Are you sure you want to delete importer configuration {{deleteValueName}}? If removed, data will not be recoverable.
      </template>
      <template v-slot:actions>
        <v-spacer></v-spacer>
        <v-btn dark color="delete" @click="submitDeleteValue()">
          <v-icon>delete_forever</v-icon>{{ $t('Button.Delete') }}
        </v-btn>
        <v-btn color="secondary" @click="deleteValueModal = false" outline>{{ $t('Button.Cancel') }}</v-btn>
      </template>
    </app-modal>

    <app-modal v-model="deleteOutputModal" max-width="800" persistent>
      <template v-slot:title>
        Delete Grade Output Configuration
      </template>
      <template v-slot:text>
        Are you sure you want to delete importer configuration {{deleteOutputName}}? If removed, data will not be recoverable.
      </template>
      <template v-slot:actions>
        <v-spacer></v-spacer>
        <v-btn dark color="delete" @click="submitDeleteOutput()">
          <v-icon>delete_forever</v-icon>{{ $t('Button.Delete') }}
        </v-btn>
        <v-btn color="secondary" @click="deleteOutputModal = false" outline>{{ $t('Button.Cancel') }}</v-btn>
      </template>
    </app-modal>

    <app-confirmation-modal v-model="warningModel" max-width="500" persistent>
      <template v-slot:title>
        {{ errorTitle }}
      </template>
      <template v-slot:text>
        {{ errorMessage }}
      </template>
      <template v-slot:actions>
        <v-spacer></v-spacer>
        <v-btn color="secondary" @click="warningModel = false" outline>{{ $t('Button.Cancel') }}</v-btn>
      </template>
    </app-confirmation-modal>

    <v-card color="grey lighten-4">
      <v-snackbar style="text-align:center" :color="snackColor" :timeout="null" top v-model="snackbar">
        {{ snackMessage }}
        <v-btn dark flat @click.native="snackbar = false">{{ $t('Button.Close') }}</v-btn>
      </v-snackbar>
    </v-card>
  </v-layout>
</template>

<script>
  import Subheader from '@/components/generic/Subheader'
  import AppModal from '@/components/generic/AppModal'
  import AppConfirmationModal from '@/components/generic/AppConfirmationModal'

  import {
    createNamespacedHelpers,
  } from 'vuex'
  const grades = createNamespacedHelpers('grades')
  const authenticate = createNamespacedHelpers('authenticate')

  export default {
    components: {
      Subheader,
      'app-modal': AppModal,
      'app-confirmation-modal': AppConfirmationModal,
    },
    props: {
      tenantName: {
        type: String,
        default: '',
      },
      tenantId: {
        type: Number,
        default: null,
      },
    },
    data() {
      return {
        validValue: true,
        validOutput: true,
        valueHeaders: [{
          text: 'Grade Value',
          value: 'GradeValue',
          align: 'left',
        },
        {
          text: 'Should Filter out <i class="v-icon clickable ml-1 material-icons theme--light primary--text" style="display:contents">info_outline</i>',
          value: 'ShouldFilter',
          align: 'left',
          showtooltip: true,
        },
        {
          text: 'Actions',
          value: null,
          align: 'right',
        },
        ],
        outputHeaders: [{
          text: 'Code',
          value: 'Code',
          align: 'left',
        },
        {
          text: 'Description',
          value: 'Description',
          align: 'left',
        },
        {
          text: 'Grade Values',
          value: 'ContinuousDataGrades',
          align: 'left',
        },
        {
          text: 'Actions',
          value: null,
          align: 'right',
        },
        ],
        gradeValueRule: [
          (v) => {
            if (v) {
              return /^\d+$/.test(v) || 'Grade Value must be numerical'
            } else
              return 'Is required, cannot be null'
          },
        ],
        nullRule: [
          (v) => !!v || 'Is required, cannot be null',
        ],
        nullRuleSelect: [
          (v) => !!v.length || 'Is required, cannot be null',
        ],
        menu: false,
        warningModel: false,
        errorTitle: '',
        errorMessage: '',
        snackbar: false,
        snackColor: '',
        snackMessage: '',
        searchValue: '',
        searchOutput: '',
        addValueModal: false,
        addOutputModal: false,
        editValueModal: false,
        editOutputModal: false,
        deleteValueModal: false,
        deleteOutputModal: false,
        addGradeValue: {
          GradeValue: null,
          ShouldFilter: false,
        },
        editGradeValue: {
          Id: null,
          GradeValue: null,
          ShouldFilter: false,
          GradeConfigurationId: null,
        },
        deleteGradeValueId: null,
        addGradeOutput: {
          Code: '',
          Description: '',
          Values: [],
        },
        editGradeOutput: {
          Id: null,
          Code: '',
          Description: '',
          Values: [],
        },
        deleteGradeOutputId: null,
        editValueCheckList: [],
        editOutputCheckList: [],
        editOutputValueItem: [],
        deleteValueName: '',
        deleteOutputName: '',
      }
    },
    mounted() {
      if (!this.gradeData || !this.gradeData.length) {
        this.getGradeDataAndConfiguration(this.tenantId ? this.tenantId : this.currentUserGroup.id)
      }
    },
    computed: {
      ...grades.mapGetters(['ContinuousDataGrades', 'ContinuousGradeConfigurations', 'allValueItem']),
      ...authenticate.mapGetters(['currentUserGroup']),
      titleName() {
        return `${this.tenantName ? this.tenantName : this.currentUserGroup.name} Continuous Data Grades Management`
      },
    },
    methods: {
      ...grades.mapActions(['getGradeDataAndConfiguration', 'addGradeData', 'updateGradeData', 'deleteGradeData', 'addGradeConfiguration', 'updateGradeConfiguration', 'deleteGradeConfiguration']),
      getFilterString(filter) {
        return filter ? 'Yes' : 'No'
      },
      getValueString(values) {
        let arr = []
        values.forEach(e => {
          arr.push(e.GradeValue)
        })
        return arr
      },
      addValue() {
        this.$refs.formValue.resetValidation()
        this.addValueModal = true
        this.addGradeValue = {
          GradeValue: null,
          ShouldFilter: false,
        }
      },
      addOutput() {
        this.$refs.formOutput.resetValidation()
        this.addOutputModal = true
        this.addGradeOutput = {
          Code: '',
          Description: '',
          Values: [],
        }
      },
      editValue(data) {
        this.$refs.formValue.resetValidation()
        let targetData = this.ContinuousDataGrades.find(x => x.Id == data.Id)
        if (!targetData.GradeConfigurationId) {
          this.editValueModal = true
          this.editValueCheckList = JSON.parse(JSON.stringify(this.ContinuousDataGrades))
          this.editValueCheckList.splice(this.editValueCheckList.findIndex(x => x.GradeValue == data.GradeValue), 1)
          this.editGradeValue = {
            Id: data.Id,
            GradeValue: data.GradeValue,
            ShouldFilter: data.ShouldFilter,
            GradeConfigurationId: data.GradeConfigurationId,
          }
        } else {
          this.warningModel = true
          this.errorTitle = this.$t('ContinuousDataGradesManage.unableUpdate')
          this.errorMessage = this.$t('ContinuousDataGradesManage.unableUpdateMsg')
        }
      },
      editOutput(data) {
        this.$refs.formOutput.resetValidation()
        let Values = []
        data.ContinuousDataGrades.forEach(x => Values.push(x.GradeValue))
        this.editOutputModal = true
        this.editGradeOutput.Id = data.Id
        this.editGradeOutput.Code = data.Code
        this.editGradeOutput.Description = data.Description
        this.editGradeOutput.Values = Values
        this.editOutputCheckList = JSON.parse(JSON.stringify(this.ContinuousGradeConfigurations))
        this.editOutputCheckList.splice(this.editOutputCheckList.findIndex(x => x.Code == data.Code), 1)
        this.editOutputValueItem = JSON.parse(JSON.stringify(this.allValueItem))
        Values.forEach(x => this.editOutputValueItem.push(x))
      },
      haveConfiguration(data) {
        let targetData = this.ContinuousDataGrades.find(x => x.Id == data.Id)
        if (!targetData.GradeConfigurationId)
          return false
        else
          return true
      },
      deleteValue(data) {
        this.deleteValueName = ''
        let targetData = this.ContinuousDataGrades.find(x => x.Id == data.Id)
        if (!targetData.GradeConfigurationId) {
          this.deleteValueModal = true
          this.deleteGradeValueId = data.Id
          this.deleteValueName = data.GradeValue
        } else {
          this.warningModel = true
          this.errorTitle = this.$t('ContinuousDataGradesManage.unableDelete')
          this.errorMessage = this.$t('ContinuousDataGradesManage.unableDeleteMsg')
        }
      },
      deleteOutput(data) {
        this.deleteOutputName = ''
        this.deleteOutputModal = true
        this.deleteGradeOutputId = data.Id
        this.deleteOutputName = data.Code
      },
      submitAddValue() {
        if (this.addGradeValue.GradeValue) {
          let repeat = this.ContinuousDataGrades.findIndex(e => e.GradeValue == this.addGradeValue.GradeValue)
          if (repeat == -1) {
            let options = {
              data: {
                Id: null,
                GradeValue: this.addGradeValue.GradeValue,
                ShouldFilter: this.addGradeValue.ShouldFilter,
                TenantId: this.tenantId ? this.tenantId : this.currentUserGroup.id,
                GradeConfigurationId: null,
              },
              successCb: () => {
                this.snackbar = true
                this.snackColor = 'success'
                this.snackMessage = this.$t('ContinuousDataGradesManage.saveSuccessMsg')
                this.addValueModal = false
                this.addGradeValue = {
                  GradeValue: null,
                  ShouldFilter: false,
                }
              },
              failedCb: (error) => {
                this.warningModel = true
                this.errorTitle = this.$t('ContinuousDataGradesManage.errorMessage')
                this.errorMessage = error
              },
            }
            this.addGradeData(options)
          } else {
            this.snackbar = true
            this.snackColor = 'error'
            this.snackMessage = this.$t('ContinuousDataGradesManage.valueExists')
          }
        } else {
          this.snackbar = true
          this.snackColor = 'error'
          this.snackMessage = this.$t('ContinuousDataGradesManage.couldNotBeNull')
        }
      },
      submitAddOutput() {
        if (this.addGradeOutput.Code && this.addGradeOutput.Description && this.addGradeOutput.Values && this.addGradeOutput.Values.length) {
          let repeat = this.ContinuousGradeConfigurations.findIndex(e => e.Code == this.addGradeOutput.Code)
          if (repeat == -1) {
            let gradeList = []
            this.addGradeOutput.Values.forEach(x => {
              gradeList.push(this.ContinuousDataGrades.find(y => y.GradeValue == x))
            })
            let options = {
              data: {
                Id: null,
                Code: this.addGradeOutput.Code,
                Description: this.addGradeOutput.Description,
                TenantId: this.tenantId ? this.tenantId : this.currentUserGroup.id,
                ContinuousDataGrades: gradeList,
              },
              successCb: () => {
                this.snackbar = true
                this.snackColor = 'success'
                this.snackMessage = this.$t('ContinuousDataGradesManage.saveSuccessMsg')
                this.addOutputModal = false
                this.addGradeOutput = {
                  Code: '',
                  Description: '',
                  Values: [],
                }
              },
              failedCb: (error) => {
                this.warningModel = true
                this.errorTitle = this.$t('ContinuousDataGradesManage.errorMessage')
                this.errorMessage = error
              },
            }
            this.addGradeConfiguration(options)
          } else {
            this.snackbar = true
            this.snackColor = 'error'
            this.snackMessage = this.$t('ContinuousDataGradesManage.codeExists')
          }
        } else {
          this.snackbar = true
          this.snackColor = 'error'
          this.snackMessage = this.$t('ContinuousDataGradesManage.couldNotBeNull')
        }
      },
      submitEditValue() {
        if (this.editGradeValue.GradeValue) {
          if (this.editGradeValue.GradeConfigurationId && this.editGradeValue.ShouldFilter) {
            this.warningModel = true
            this.errorTitle = this.$t('ContinuousDataGradesManage.errorMessage')
            this.errorMessage = this.$t('ContinuousDataGradesManage.shouldFilterOutCouldNotBeYes')
          } else {
            let repeat = this.editValueCheckList.findIndex(e => e.GradeValue == this.editGradeValue.GradeValue)
            if (repeat == -1) {
              let options = {
                data: {
                  Id: this.editGradeValue.Id,
                  GradeValue: this.editGradeValue.GradeValue,
                  ShouldFilter: this.editGradeValue.ShouldFilter,
                  TenantId: this.tenantId ? this.tenantId : this.currentUserGroup.id,
                  GradeConfigurationId: this.editGradeValue.GradeConfigurationId,
                },
                successCb: () => {
                  this.snackbar = true
                  this.snackColor = 'success'
                  this.snackMessage = this.$t('ContinuousDataGradesManage.saveSuccessMsg')
                  this.editValueModal = false
                  this.editGradeValue = {
                    Id: null,
                    GradeValue: null,
                    ShouldFilter: false,
                    GradeConfigurationId: null,
                  }
                },
                failedCb: (error) => {
                  this.warningModel = true
                  this.errorTitle = this.$t('ContinuousDataGradesManage.errorMessage')
                  this.errorMessage = error
                },
              }
              this.updateGradeData(options)
            } else {
              this.snackbar = true
              this.snackColor = 'error'
              this.snackMessage = this.$t('ContinuousDataGradesManage.valueExists')
            }
          }

        } else {
          this.snackbar = true
          this.snackColor = 'error'
          this.snackMessage = this.$t('ContinuousDataGradesManage.couldNotBeNull')
        }
      },
      submitEditOutput() {
        if (this.editGradeOutput.Code && this.editGradeOutput.Description && this.editGradeOutput.Values && this.editGradeOutput.Values.length) {
          let repeat = this.editOutputCheckList.findIndex(e => e.Code == this.editGradeOutput.Code)
          if (repeat == -1) {
            let gradeList = []
            this.editGradeOutput.Values.forEach(x => {
              gradeList.push(this.ContinuousDataGrades.find(y => y.GradeValue == x))
            })
            let options = {
              data: {
                Id: this.editGradeOutput.Id,
                Code: this.editGradeOutput.Code,
                Description: this.editGradeOutput.Description,
                TenantId: this.tenantId ? this.tenantId : this.currentUserGroup.id,
                ContinuousDataGrades: gradeList,
              },
              successCb: () => {
                this.snackbar = true
                this.snackColor = 'success'
                this.snackMessage = this.$t('ContinuousDataGradesManage.saveSuccessMsg')
                this.editOutputModal = false
                this.editGradeOutput = {
                  Id: null,
                  Code: '',
                  Description: '',
                  Values: [],
                }
              },
              failedCb: (error) => {
                this.warningModel = true
                this.errorTitle = this.$t('ContinuousDataGradesManage.errorMessage')
                this.errorMessage = error
              },
            }
            this.updateGradeConfiguration(options)
          } else {
            this.snackbar = true
            this.snackColor = 'error'
            this.snackMessage = this.$t('ContinuousDataGradesManage.codeExists')
          }
        } else {
          this.snackbar = true
          this.snackColor = 'error'
          this.snackMessage = this.$t('ContinuousDataGradesManage.couldNotBeNull')
        }
      },
      submitDeleteValue() {
        let options = {
          data: this.deleteGradeValueId,
          successCb: () => {
            this.snackbar = true
            this.snackColor = 'success'
            this.snackMessage = this.$t('ContinuousDataGradesManage.deleteSuccessMsg')
            this.deleteValueModal = false
            this.deleteGradeValueId = null
          },
          failedCb: (error) => {
            this.warningModel = true
            this.errorTitle = this.$t('ContinuousDataGradesManage.errorMessage')
            this.errorMessage = error
          },
        }
        this.deleteGradeData(options)
      },
      submitDeleteOutput() {
        let options = {
          data: this.deleteGradeOutputId,
          successCb: () => {
            this.snackbar = true
            this.snackColor = 'success'
            this.snackMessage = this.$t('ContinuousDataGradesManage.deleteSuccessMsg')
            this.deleteOutputModal = false
            this.deleteGradeOutputId = null
          },
          failedCb: (error) => {
            this.warningModel = true
            this.errorTitle = this.$t('ContinuousDataGradesManage.errorMessage')
            this.errorMessage = error
          },
        }
        this.deleteGradeConfiguration(options)
      },
    },
  }
</script>

<style lang="stylus" scoped>
</style>